import React, { useCallback, useEffect, useState } from 'react';
import { getChecklistData, getUnits } from '../../api/dashboard';
import FilterSelect from '../../components/FilterSelect';
import PageHeader from '../../components/PageHeader';
import StatusMessage from '../../components/StatusMessage';
import { formatShift } from '../../utils/format';
import { adminErrorMessage } from './adminErrors';
import { keyItems, mentionedCount, pct, rankThreshold, sortByMissed, withRanking } from './insightsUtils';
import './Admin.css';

const SHIFTS = [
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
];

function KeyFact({ value, label, sub, valueClassName = '' }) {
  return (
    <div className="stat-card stat-card-static">
      <span className={`stat-value ${valueClassName}`.trim()}>{value}</span>
      <span className="stat-label">{label}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

// Aggregate handoff checklist results for one shift: key facts, then for each
// checklist item the share of handoffs that mentioned or missed it, as one bar
// (the two shares add up to 100%). Admins see
// these totals only, never an individual handoff or report sheet.
const Insights = () => {
  // null date and shift mean "the most recent shift", which the backend picks.
  const [date, setDate] = useState(null);
  const [shift, setShift] = useState(null);
  const [unitId, setUnitId] = useState('');
  const [units, setUnits] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUnits()
      .then((res) => setUnits([...(res.units || [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))))
      .catch(() => setUnits([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getChecklistData({ date, shift, unitId }));
    } catch (err) {
      setError(adminErrorMessage(err, "We couldn't load the handoff insights."));
    } finally {
      setLoading(false);
    }
  }, [date, shift, unitId]);

  useEffect(() => {
    load();
  }, [load]);

  // The controls show what's loaded until the admin picks something else.
  const shownDate = date ?? data?.date ?? '';
  const shownShift = shift ?? data?.shift ?? 'day';
  const items = sortByMissed(withRanking(data?.items, data?.handoffCount));
  const firstUnranked = items.findIndex((item) => item.ranked === false);
  const top = keyItems(items);

  return (
    <>
      <PageHeader title="Insights" />
      <div className="component-container">
        <div className="admin-content">
          <div className="search-row">
            <label className="filter-pill filter-pill-date">
              <span className="filter-pill-label">Date:</span>
              <input
                type="date"
                className="date-input"
                aria-label="Date"
                value={shownDate}
                onChange={(e) => { setDate(e.target.value || null); setShift(shownShift); }}
              />
            </label>
            <div className="segmented" role="group" aria-label="Shift">
              {SHIFTS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={`segmented-button${shownShift === s.value ? ' segmented-button-active' : ''}`}
                  aria-pressed={shownShift === s.value}
                  onClick={() => { setShift(s.value); setDate(shownDate || null); }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <FilterSelect
              label="Unit"
              value={unitId}
              onChange={setUnitId}
              options={[{ value: '', label: 'All units' }, ...units.map((u) => ({ value: u.id, label: u.name }))]}
            />
            {loading && data && <span className="search-count">Updating…</span>}
          </div>

          {error && <StatusMessage kind="error" onRetry={load}>{error}</StatusMessage>}
          {!error && !data && <StatusMessage kind="loading">Loading insights…</StatusMessage>}

          {!error && data && (
            <>
              <div className="stats-row key-facts" aria-label="Key facts">
                <KeyFact value={data.handoffCount} label="Handoffs" sub={formatShift(data.date, data.shift)} />
                <KeyFact
                  value={top ? top.mentioned.name : '—'}
                  valueClassName="stat-value-item stat-value-mentioned"
                  label="Most Mentioned"
                  sub={top ? `${pct(top.mentioned.mentionedPct)} mentioned (${mentionedCount(top.mentioned)} of ${top.mentioned.handoffCount})` : 'No handoffs yet'}
                />
                <KeyFact
                  value={top ? top.missed.name : '—'}
                  valueClassName="stat-value-item stat-value-missed"
                  label="Most Missed"
                  sub={top ? `${pct(top.missed.missedPct)} missed (${top.missed.handoffCount - mentionedCount(top.missed)} of ${top.missed.handoffCount})` : 'No handoffs yet'}
                />
              </div>

              {items.length === 0 ? (
                <StatusMessage kind="empty">No handoffs recorded for this shift yet.</StatusMessage>
              ) : (
                <div className="data-table" aria-label="Checklist items">
                  <div className="data-table-header">
                    <span className="col-item">Checklist item</span>
                    <span className="col-split">Mentioned / Missed</span>
                    <span className="col-count">Handoffs</span>
                  </div>
                  {items.map((item, index) => (
                    <React.Fragment key={item.name}>
                      {index === firstUnranked && (
                        <div className="data-table-group">
                          Too few handoffs to rank (fewer than {rankThreshold(data.handoffCount)})
                        </div>
                      )}
                      <div className="data-row">
                        <span className="col-item person-name">{item.name}</span>
                        <span className="col-split">
                          <span className="pct-mentioned">{pct(item.mentionedPct)}</span>
                          <span className="pct-bar" aria-hidden="true">
                            <span className="pct-bar-fill pct-bar-mentioned" style={{ width: `${item.mentionedPct ?? 0}%` }} />
                            <span className="pct-bar-fill pct-bar-missed" style={{ width: `${item.missedPct ?? 0}%` }} />
                          </span>
                          <span className="pct-missed">{pct(item.missedPct)}</span>
                        </span>
                        <span className="col-count">{mentionedCount(item)} of {item.handoffCount}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
              <p className="admin-hint">
                Each handoff is checked against the items that apply to that patient, so each percentage is out of the
                handoffs that included that item. Handoffs recorded 1 PM–1 AM count toward the day shift and 1 AM–1 PM
                toward the night shift, so early or late handoffs land in the right shift. A night shift is labeled with
                the date it starts. An item in fewer than {rankThreshold(data.handoffCount)} handoffs isn't ranked or
                picked as most mentioned or most missed, since a handful of handoffs can swing its percentage.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Insights;
